import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Crown, ShoppingBag, Download, Heart, Sparkles, 
  Gift, FileText, Music, Video, Mic, Calendar, Phone, Image, Palette, Coins, Plus,
  Package, Star, Zap, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import FadeImage from '../components/FadeImage';
import PurchaseDialog from '../components/PurchaseDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== ELLA'S EIGEN SHOP FOTO'S - DEFINITIEF HARDCODED ==============
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SHOP_IMAGES = {
  prompt_guide: `${API_BASE}/api/media/fe32409a-a72c-478a-b393-d4e56384e198`,
  hd_pack_10: `${API_BASE}/api/media/1422b953-338b-4c04-aeb9-269cb085852c`,
  hd_pack_50: `${API_BASE}/api/media/ffc467ae-fea1-4c09-bc20-3cfd2ec28b2c`,
  video_pack: `${API_BASE}/api/media/f302dc32-c96d-4948-b797-b7ade5891a11`,
  wallpaper_pack: `${API_BASE}/api/media/ee68a5ee-26b5-4f4b-b47a-40bdf4839d28`,
  "4k_collection": `${API_BASE}/api/media/87bbba72-58e7-43d8-90e9-705e1397c7a1`,
  ai_girlfriend: `${API_BASE}/api/media/bcb03adf-657b-4484-af91-3b51cfef06fa`,
  featured_product: `${API_BASE}/api/media/2525736b-b71d-4cc3-ad80-4bf115996135`,
  bronze: `${API_BASE}/api/media/ebd63b84-575a-48d2-a05b-33b154a62162`,
  silver: `${API_BASE}/api/media/29879b2b-b65d-4245-8093-e6b10be8497b`,
  gold: `${API_BASE}/api/media/b47d028f-1368-451e-92ae-731882b5fc89`,
  platinum: `${API_BASE}/api/media/ab593693-bf48-4d47-884e-cbe6b492aea2`,
};

export default function Shop() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('packages');
  const [purchasing, setPurchasing] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // CONTENT PACKAGES
  const PACKAGES = [
    { 
      id: 'bronze', 
      name: 'Bronze', 
      price: 5, 
      photos: 5, 
      videos: 0,
      color: '#CD7F32',
      hasImage: true
    },
    { 
      id: 'silver', 
      name: 'Silver', 
      price: 10, 
      photos: 10, 
      videos: 2,
      color: '#C0C0C0',
      hasImage: true
    },
    { 
      id: 'gold', 
      name: 'Gold', 
      price: 15, 
      photos: 20, 
      videos: 5,
      color: '#D4AF37',
      popular: true,
      hasImage: true
    },
    { 
      id: 'platinum', 
      name: 'Platinum', 
      price: 20, 
      photos: 50, 
      videos: 10,
      color: '#E5E4E2',
      hasImage: true
    },
  ];

  // SPECIALE PRODUCTEN
  const SPECIALS = [
    { id: 'lifetime', name: 'Lifetime Access', price: 80, description: t('lifetime.subtitle', 'Unlimited access forever'), icon: Crown, badge: 'BEST DEAL' },
    { id: 'prompt_guide', name: t('shop.promptGuide.title', 'The Ultimate Prompt Guide'), price: 49, description: t('shop.promptGuide.subtitle', "Ella Gold's AI Art Secrets"), icon: FileText, badge: 'BESTSELLER', hasImage: true },
    { id: 'ai_girlfriend', name: t('shop.customAI.title', 'Custom AI Art Model'), price: 350, description: t('shop.customAI.description', 'Your own AI art style + training'), icon: Heart, badge: 'EXCLUSIVE', hasImage: true },
  ];

  // DIGITALE PRODUCTEN
  const DIGITAL_PRODUCTS = [
    { id: 'hd_pack_10', name: '10 HD Photo Pack', price: 10, description: 'High-res collection', icon: Image, hasImage: true },
    { id: 'hd_pack_50', name: '50 HD Photo Pack', price: 30, description: 'Mega collection - best value!', icon: Image, badge: 'VALUE', hasImage: true },
    { id: 'video_pack', name: 'Video Collection', price: 20, description: '5 exclusive videos', icon: Video, hasImage: true },
    { id: 'wallpaper_pack', name: 'Wallpaper Pack', price: 5, description: '20 phone + desktop wallpapers', icon: Phone, hasImage: true },
    { id: '4k_collection', name: '4K Collection', price: 40, description: 'Ultra HD prints', icon: Palette, hasImage: true },
  ];

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setCreditBalance(res.data?.balance || 0))
        .catch(() => {});
    }
  }, [user]);

  const getShopImage = (key) => {
    return DEFAULT_SHOP_IMAGES[key] || DEFAULT_SHOP_IMAGES.featured_product;
  };

  const handleBuy = (product) => {
    if (!user) {
      toast.error(t('tokens.loginRequired', 'Please login first'));
      navigate('/login');
      return;
    }
    setConfirmDialog(product);
  };

  const confirmPurchase = async (finalPrice) => {
    if (!confirmDialog) return;
    
    const product = confirmDialog;
    const priceToUse = finalPrice ?? product.price;
    
    if (creditBalance < priceToUse) {
      toast.error(t('tokens.insufficientTokens', "You don't have enough tokens"));
      setConfirmDialog(null);
      navigate('/credits');
      return;
    }
    
    setPurchasing(product.id);
    setConfirmDialog(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/credits/spend`, {
        product_id: product.id,
        credits_amount: product.price  // Send original price, backend calculates discount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCreditBalance(response.data.new_balance);
        const savedMsg = response.data.discount_amount > 0 
          ? ` (Je bespaarde ${response.data.discount_amount} tokens!)` 
          : '';
        toast.success(`🎉 ${product.name} ${t('tokens.purchaseSuccess', 'purchased')}!${savedMsg}`);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(t('tokens.purchaseFailed', 'Purchase failed'));
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-20">
        {/* Token Balance */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8 p-4 glass rounded-xl border border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Coins className="w-6 h-6 text-black" />
            </div>
            <div>
              <p className="text-sm text-gray-400">{t('tokens.yourBalance', 'Your Balance')}</p>
              <p className="text-2xl font-bold text-[#D4AF37]">{creditBalance}</p>
            </div>
          </div>
          <Button onClick={() => navigate('/credits')} className="bg-[#D4AF37] hover:bg-[#B8963A] text-black w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            {t('tokens.buyTokens', 'Buy Tokens')}
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">
            <ShoppingBag className="w-8 h-8 sm:w-10 sm:h-10 text-[#D4AF37] inline mr-2" />
            {t('shop.title', 'Digital Art')} <span className="text-[#D4AF37]">{t('shop.titleAccent', 'Shop')}</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            {t('shop.subtitle', 'Exclusive digital products and art collections')}
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-center bg-white/5 border border-white/10 mb-8 flex-wrap h-auto gap-2 p-2">
            <TabsTrigger value="packages" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black flex-1 sm:flex-none">
              <Package className="w-4 h-4 mr-2" />
              {t('packages.tabs.oneTime', 'Packages')}
            </TabsTrigger>
            <TabsTrigger value="specials" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black flex-1 sm:flex-none">
              <Star className="w-4 h-4 mr-2" />
              {t('packages.tabs.specials', 'Specials')}
            </TabsTrigger>
            <TabsTrigger value="digital" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black flex-1 sm:flex-none">
              <Download className="w-4 h-4 mr-2" />
              {t('shop.tabs.digital', 'Digital Art')}
            </TabsTrigger>
          </TabsList>

          {/* PACKAGES TAB */}
          <TabsContent value="packages">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {PACKAGES.map((pkg) => (
                <div 
                  key={pkg.id}
                  className={`relative p-5 rounded-xl border transition-all hover:-translate-y-1 ${
                    pkg.popular 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                      : 'border-white/10 bg-white/5'
                  }`}
                  data-testid={`package-${pkg.id}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full">
                      {t('packages.popular', 'POPULAR')}
                    </div>
                  )}
                  
                  {pkg.hasImage && (
                    <div className="aspect-square rounded-lg overflow-hidden mb-4">
                      <FadeImage 
                        src={getShopImage(pkg.id)}
                        alt={pkg.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="text-center">
                    <h3 className="text-lg font-bold mb-1" style={{ color: pkg.color }}>{pkg.name}</h3>
                    <p className="text-sm text-gray-400 mb-3">{pkg.photos} photos {pkg.videos > 0 ? `+ ${pkg.videos} videos` : ''}</p>
                    
                    <div className="flex justify-center gap-4 mb-4 text-sm">
                      <div className="text-center">
                        <div className="text-xl font-bold text-white">{pkg.photos}</div>
                        <div className="text-xs text-gray-500">photos</div>
                      </div>
                      {pkg.videos > 0 && (
                        <div className="text-center">
                          <div className="text-xl font-bold text-white">{pkg.videos}</div>
                          <div className="text-xs text-gray-500">videos</div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-center gap-1 mb-3">
                      <Coins className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-2xl font-bold text-[#D4AF37]">{pkg.price}</span>
                    </div>
                    
                    <Button 
                      onClick={() => handleBuy(pkg)}
                      disabled={purchasing === pkg.id}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                    >
                      {purchasing === pkg.id ? t('common.loading', 'Loading...') : t('packages.buyNow', 'Buy Now')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* SPECIALS TAB */}
          <TabsContent value="specials">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {SPECIALS.map((item) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.id}
                    className="p-6 glass rounded-xl border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all"
                    data-testid={`special-${item.id}`}
                  >
                    {item.badge && (
                      <span className="inline-block px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full mb-4">
                        {item.badge}
                      </span>
                    )}
                    
                    {item.hasImage ? (
                      <div className="aspect-video rounded-lg overflow-hidden mb-4">
                        <FadeImage 
                          src={getShopImage(item.id)}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center mb-4">
                        <Icon className="w-8 h-8 text-[#D4AF37]" />
                      </div>
                    )}
                    
                    <h3 className="text-xl font-bold mb-2">{item.name}</h3>
                    <p className="text-sm text-gray-400 mb-4">{item.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Coins className="w-5 h-5 text-[#D4AF37]" />
                        <span className="text-2xl font-bold text-[#D4AF37]">{item.price}</span>
                      </div>
                      <Button 
                        onClick={() => handleBuy(item)}
                        disabled={purchasing === item.id}
                        className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                      >
                        {t('packages.buyNow', 'Buy Now')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* DIGITAL TAB */}
          <TabsContent value="digital">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {DIGITAL_PRODUCTS.map((item) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.id}
                    className="p-5 glass rounded-xl border border-white/10 hover:border-[#D4AF37]/50 transition-all"
                    data-testid={`digital-${item.id}`}
                  >
                    {item.badge && (
                      <span className="inline-block px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full mb-3">
                        {item.badge}
                      </span>
                    )}
                    
                    {item.hasImage && (
                      <div className="aspect-video rounded-lg overflow-hidden mb-4">
                        <FadeImage 
                          src={getShopImage(item.id)}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    <h3 className="text-lg font-semibold mb-1">{item.name}</h3>
                    <p className="text-sm text-gray-400 mb-4">{item.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Coins className="w-5 h-5 text-[#D4AF37]" />
                        <span className="text-xl font-bold text-[#D4AF37]">{item.price}</span>
                      </div>
                      <Button 
                        onClick={() => handleBuy(item)}
                        disabled={purchasing === item.id}
                        size="sm"
                        className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                      >
                        {t('shop.buyNow', 'Buy Now')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Quick Info */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div className="p-4 glass rounded-xl">
            <Zap className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <h4 className="font-semibold mb-1">{t('tokens.features.instantDelivery', 'Instant delivery')}</h4>
            <p className="text-xs text-gray-400">{t('packages.oneTime.starter.features.1', 'Instant download')}</p>
          </div>
          <div className="p-4 glass rounded-xl">
            <Download className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <h4 className="font-semibold mb-1">HD Downloads</h4>
            <p className="text-xs text-gray-400">{t('memberships.dailyTease.features.0', '1 new artwork daily')}</p>
          </div>
          <div className="p-4 glass rounded-xl">
            <Gift className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <h4 className="font-semibold mb-1">€30 = Gold</h4>
            <p className="text-xs text-gray-400">{t('goldChat.upgradeNow', 'Upgrade to Gold')}</p>
          </div>
        </div>
      </div>

      {/* Confirm Purchase Dialog */}
      <PurchaseDialog 
        product={confirmDialog}
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmPurchase}
        creditBalance={creditBalance}
        purchasing={purchasing === confirmDialog?.id}
        t={t}
      />
    </div>
  );
}
